/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useState } from 'react';
import Ajv from 'ajv';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { callApi } from 'api/helpers/api.helpers';
import { removeLeadingWhitespaceForValue } from '../../../shared/utils/stringUtils';
import AssistiveText from './AssistiveText';
import TextWidget from './TextWidget';
import draftApi from '../../../api/draft.api';
import { draftActions } from '../../draft/draftReducer';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import useInitialSubmissionProcedure from '../../../procedure/useInitialSubmissionProcedure';

const ajv = new Ajv();

export default function UserReferenceWidget(props) {
  const [backendValidation, setBackendValidation] = useState({ INFO: [], WARNING: [], ERROR: [] });
  const [hasWarning, setHasWarning] = useState(false);
  const [hasSchemaErrors, setHasSchemaErrors] = useState(false);
  const { formContext = {}, onChange, uiSchema = {}, schema = {} } = props;
  const { dispatchDraftState, submissionId } = formContext;
  const disableExistingUserReferenceValidation = uiSchema?.disableExistingUserReferenceValidation || false;
  const { t } = useTranslation();
  const initialSubmissionProcedure = useInitialSubmissionProcedure();

  const onChangeDebounced = useDebouncedCallback(
    async (value) => {
      const id = submissionId || '';
      const validByJsonSchema = ajv.validate(schema, value);
      setHasSchemaErrors(!validByJsonSchema);

      if (validByJsonSchema) {
        if (!initialSubmissionProcedure && !disableExistingUserReferenceValidation) {
          const result = await callApi(draftApi.validateExistingUserReference)({ id, userReference: value });
          setBackendValidation(result?.validation);
          if (result?.validation?.WARNING?.userReference != null) {
            setHasWarning(true);
          } else {
            setHasWarning(false);
          }
        }
      }
    },
    500,
    {},
    [setHasSchemaErrors],
  );

  return (
    <div>
      <TextWidget
        {...props}
        size="small"
        hasWarning={hasWarning}
        hasError={hasSchemaErrors}
        onChange={async (v) => {
          const value = removeLeadingWhitespaceForValue(v);
          onChange(value);
          onChangeDebounced(value);
        }}
        onBlur={(_, v) => {
          const value = removeLeadingWhitespaceForValue(v);
          if (ajv.validate(schema, value)) {
            dispatchDraftState({ type: draftActions.USER_REFERENCE_SET });
          }
        }}
      />
      {hasWarning && (
        <AssistiveText severity={uiSchema[uiSchemaProperties.UI_HELP_SEVERITY] || 'warning'}>
          {t(backendValidation?.WARNING?.userReference)}
        </AssistiveText>
      )}
      {hasSchemaErrors && (
        <AssistiveText severity={uiSchema[uiSchemaProperties.UI_HELP_SEVERITY] || 'warning'}>
          {t('submission.basicfiliginfo.businessrule.userreference.not-autosaved')}
        </AssistiveText>
      )}
    </div>
  );
}
